import { useState } from "react";
import classNames from "classnames";
import Slider from "components/DesignSystem/Slider/Slider";
import { Button } from "components/DesignSystem/Button/Button";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { EditPencil } from "components/icons/EditPencil";
import { Trash20 } from "components/icons/Trash20";
import { PlusIcon } from "components/icons/PlusIcon";
import { CaretDown } from "components/icons/CaretDown";
import { Spinner } from "components/DesignSystem/Loader/loader";
import { useToast } from "hooks/useToast";
import {
  useListFinancialAdjustmentsQuery,
  useDeleteFinancialAdjustmentMutation,
} from "store/apis/estimatedTax";
import {
  FinancialAdjustment,
  FinancialAdjustmentMonth,
} from "types/Models/estimatedTax";
import { BackendError } from "types/utils/error";
import { MONTH_OPTIONS } from "./constants";
import { FinancialAdjustmentFormModal } from "./FinancialAdjustmentFormModal";

type Props = {
  show: boolean;
  onClose: () => void;
  entityId: string;
  year: string;
  month: number | null;
  defaultType: "INCOME" | "EXPENSE";
  isAdmin: boolean;
};

export const FinancialAdjustmentSlider = ({
  show,
  onClose,
  entityId,
  year,
  month,
  defaultType,
  isAdmin,
}: Props) => {
  const { alertToast, successToast } = useToast();

  const [formOpen, setFormOpen] = useState(false);
  const [editingAdj, setEditingAdj] = useState<FinancialAdjustment | null>(
    null
  );
  const [formType, setFormType] = useState<"INCOME" | "EXPENSE">(defaultType);
  const [openSections, setOpenSections] = useState({
    income: true,
    expense: true,
  });

  const monthOption = month != null ? MONTH_OPTIONS[month - 1] : null;
  const monthCode = (monthOption?.value ??
    null) as FinancialAdjustmentMonth | null;
  const monthLabel = monthOption?.label ?? "";

  const { data: adjustments = [], isLoading } =
    useListFinancialAdjustmentsQuery(
      { entityId, year, month: monthCode ?? undefined },
      { skip: !show || !monthCode }
    );

  const [deleteAdj, { isLoading: isDeleting }] =
    useDeleteFinancialAdjustmentMutation();

  const incomeAdjs = adjustments.filter((a) => a.type === "INCOME");
  const expenseAdjs = adjustments.filter((a) => a.type === "EXPENSE");

  const openAdd = (type: "INCOME" | "EXPENSE") => {
    setEditingAdj(null);
    setFormType(type);
    setFormOpen(true);
  };

  const openEdit = (adj: FinancialAdjustment) => {
    setEditingAdj(adj);
    setFormType(adj.type);
    setFormOpen(true);
  };

  const handleDelete = async (adjId: string) => {
    try {
      await deleteAdj({ entityId, adjustmentId: adjId }).unwrap();
      successToast({ message: "Adjustment deleted" });
    } catch (e) {
      alertToast({
        message:
          (e as BackendError)?.data?.error?.message ||
          "Failed to delete adjustment",
      });
    }
  };

  const toggleSection = (
    section: "income" | "expense",
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const renderAdjRow = (adj: FinancialAdjustment) => (
    <div
      key={adj.id}
      className="t-flex t-items-center t-justify-between t-py-2.5 t-border-b t-border-neutral-10 last:t-border-b-0"
    >
      <div className="t-flex t-flex-col t-gap-1 t-min-w-0">
        <span className="t-text-body-sm t-text-text-100 t-truncate">
          {adj.title}
        </span>
        {adj.description && (
          <span className="t-text-body-sm t-text-text-30">
            {adj.description}
          </span>
        )}
      </div>
      <div className="t-flex t-items-center t-gap-2 t-shrink-0 t-ml-4">
        <span className="t-font-medium">
          <AmountSuperScript amount={adj.amount} />
        </span>
        {isAdmin && (
          <span className="t-flex t-items-center t-gap-0.5">
            <Button
              customType="ghost_icon"
              size="small"
              onClick={() => openEdit(adj)}
            >
              <EditPencil className="t-text-text-30" />
            </Button>
            <Button
              customType="ghost_icon"
              size="small"
              onClick={() => handleDelete(adj.id)}
              disabled={isDeleting}
            >
              <Trash20 />
            </Button>
          </span>
        )}
      </div>
    </div>
  );

  const renderSection = (
    title: string,
    items: FinancialAdjustment[],
    type: "INCOME" | "EXPENSE",
    sectionKey: "income" | "expense"
  ) => {
    const isOpen = openSections[sectionKey];
    return (
      <div className="t-rounded-lg t-border t-border-neutral-10 t-overflow-hidden">
        <div
          className="t-flex t-items-center t-justify-between t-px-4 t-py-3 t-bg-surface-lighter-grey t-cursor-pointer"
          onClick={(e) => toggleSection(sectionKey, e)}
        >
          <div className="t-flex t-items-center t-gap-2">
            <CaretDown
              className={classNames(
                "t-text-text-30 t-transition-transform t-duration-200",
                { "t-rotate-180": !isOpen }
              )}
            />
            <span className="t-font-semibold t-text-text-100">{title}</span>
            {items.length > 0 && (
              <span className="t-text-caption t-font-medium t-bg-neutral-10 t-text-text-60 t-rounded-full t-px-1.5 t-py-0.5">
                {items.length}
              </span>
            )}
          </div>
          {isAdmin && (
            <Button
              customType="ghost_icon"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                openAdd(type);
              }}
            >
              <PlusIcon />
            </Button>
          )}
        </div>

        {isOpen && (
          <div className="t-px-4">
            {items.length === 0 ? (
              <p className="t-text-body-sm t-text-text-30 t-py-3">
                No {title.toLowerCase()} adjustments for this month.
              </p>
            ) : (
              items.map(renderAdjRow)
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Slider.Root open={show} onOpenChange={(o) => !o && onClose()}>
        <Slider.Content width={420}>
          <Slider.Header>
            <Slider.Title>Financial Adjustments — {monthLabel}</Slider.Title>
            <Slider.Close />
          </Slider.Header>
          <Slider.Body className="t-flex t-flex-col t-gap-4">
            {isLoading ? (
              <div className="t-flex t-justify-center t-py-8">
                <Spinner size="small" />
              </div>
            ) : (
              <>
                {renderSection("Income", incomeAdjs, "INCOME", "income")}
                {renderSection("Expense", expenseAdjs, "EXPENSE", "expense")}
              </>
            )}
          </Slider.Body>
        </Slider.Content>
      </Slider.Root>

      {monthCode && (
        <FinancialAdjustmentFormModal
          open={formOpen}
          onClose={() => setFormOpen(false)}
          entityId={entityId}
          year={year}
          monthCode={monthCode}
          monthLabel={monthLabel}
          adjustment={editingAdj}
          defaultType={formType}
        />
      )}
    </>
  );
};
